/* Step/flow engine — TypeScript port of the prototype's flow.js.
   Pure functions over the Answers object: builds the ordered step list
   (with conditional steps), validates the current step, and exposes the
   chapter list. SurveyApp owns the actual index/answers React state. */
import type { IconName } from './icons';
import { Answers, CATEGORIES, GROUPS, Habit, HABITS, LIFE_INTRO, MARITAL, Option, realUScore } from './data';

export type StepKind =
  | 'welcome'
  | 'eventCode'
  | 'identity'
  | 'group'
  | 'session'
  | 'choice'
  | 'scale'
  | 'habit'
  | 'intro'
  | 'kids'
  | 'kidsAges'
  | 'pets'
  | 'score'
  | 'careerPick'
  | 'review';

export interface Step {
  key: string;
  kind: StepKind;
  chapter: string;
  label?: string;
  help?: string;
  title?: string;
  subtitle?: string;
  field?: keyof Answers;
  options?: Option[];
  auto?: boolean;
  row?: boolean;
  habit?: Habit;
  n?: number;
  total?: number;
}

export interface Chapter {
  id: string;
  label: string;
  icon: IconName;
  core: boolean;
}

export const CHAPTERS: Chapter[] = [
  { id: 'about', label: 'About You', icon: 'user', core: true },
  { id: 'grades', label: 'Grades', icon: 'cap', core: true },
  { id: 'habits', label: 'Attitude & Effort', icon: 'spark', core: true },
  { id: 'life', label: 'Your Future', icon: 'heart', core: true },
  { id: 'score', label: 'Score', icon: 'star', core: false },
  { id: 'career', label: 'Career', icon: 'rocket', core: false },
];

// Build the ordered step list from current answers. Conditional steps
// appear/disappear (e.g. kid ages only when kids > 0).
export function buildSteps(answers: Answers): Step[] {
  const list: Step[] = [];
  list.push({ key: 'welcome', kind: 'welcome', chapter: 'start' });

  // Event gate: omit entirely when the URL already supplied a valid code.
  if (!answers._eventLoaded) {
    list.push({
      key: 'eventCode',
      kind: 'eventCode',
      chapter: 'start',
      label: "What's your event code?",
    });
  }

  list.push({
    key: 'identity',
    kind: 'identity',
    chapter: 'about',
    label: "First, let's get to know you!",
    auto: false,
  });
  list.push({
    key: 'group',
    kind: 'group',
    chapter: 'about',
    label: 'Whose class or group are you in?',
  });
  // Session is derived from the group when it attends one session; only a
  // group attending several needs the question (mirrors the old survey).
  const pickedGroup = GROUPS.find((g) => g.id === answers.teacherId);
  if (pickedGroup && pickedGroup.sessions.length > 1) {
    list.push({
      key: 'session',
      kind: 'session',
      chapter: 'about',
      label: 'Which session are you in?',
    });
  }

  list.push({
    key: 'gpa',
    kind: 'scale',
    chapter: 'grades',
    label: "What's your GPA right now?",
    help: 'Your grade average, from 0 to 4.',
  });

  list.push({
    key: 'habitsIntro',
    kind: 'intro',
    chapter: 'habits',
    title: 'Grades aren\'t the whole story, attitude and effort count too.',
    subtitle: 'The next few questions are about how you show up: the effort you give and the attitude you bring.',
  });

  HABITS.forEach((h, i) => {
    list.push({
      key: 'habit:' + h.id,
      kind: 'habit',
      chapter: 'habits',
      habit: h,
      n: i + 1,
      total: HABITS.length,
    });
  });

  list.push({
    key: 'lifeIntro',
    kind: 'intro',
    chapter: 'life',
    title: LIFE_INTRO.title,
    subtitle: LIFE_INTRO.subtitle,
  });
  list.push({
    key: 'marital',
    kind: 'choice',
    chapter: 'life',
    label: MARITAL.prompt || 'Are you single or married?',
    field: 'maritalStatus',
    options: [
      { value: 'single', label: 'Single', note: 'Just you and your paycheck.', emoji: '🧍' },
      { value: 'married', label: MARITAL.marriedLabel, note: 'Two paychecks and a partner.', emoji: '💍' },
    ],
    auto: true,
    row: true,
  });
  list.push({ key: 'kids', kind: 'kids', chapter: 'life', label: 'How many kids will you have?', subtitle: 'You may still be in the middle of growing your family during your mid 20s.' });
  if ((answers.kidsCount || 0) > 0) {
    list.push({ key: 'kidsAges', kind: 'kidsAges', chapter: 'life', label: 'How old will your kids be?' });
  }
  list.push({ key: 'pets', kind: 'pets', chapter: 'life', label: 'Will you have any pets?' });

  list.push({ key: 'score', kind: 'score', chapter: 'score' });

  list.push({
    key: 'careerPick',
    kind: 'careerPick',
    chapter: 'career',
    label: 'Which kind of work fits you best?',
  });

  list.push({ key: 'review', kind: 'review', chapter: 'done' });
  return list;
}

export function validate(step: Step | undefined, answers: Answers): boolean {
  if (!step) return false;
  switch (step.kind) {
    case 'welcome':
      return true;
    case 'eventCode':
      // set after the code checks out against /realu-next/event-config
      return answers._eventLoaded === true;
    case 'group':
      return !!answers.teacherId;
    case 'session':
      return !!answers.sessionId;
    case 'intro':
      return true;
    case 'identity':
      return (
        (answers.firstName || '').trim().length > 0 &&
        (answers.lastName || '').trim().length > 0 &&
        !!answers.gender
      );
    case 'choice':
      return !!(step.field && answers[step.field]);
    case 'scale':
      return typeof answers.gpa === 'number';
    case 'habit':
      return !!step.habit && typeof answers.habits[step.habit.id] === 'number';
    case 'score':
      return true;
    case 'careerPick':
      return !!answers.career;
    case 'kids':
      // kidsCount starts null — the question must be answered (0 is an answer).
      return typeof answers.kidsCount === 'number';
    case 'kidsAges':
      return (
        answers.kidsAges.length === answers.kidsCount &&
        answers.kidsAges.every((a) => typeof a === 'number')
      );
    case 'pets':
      return answers._petsAnswered === true;
    case 'review':
      return true;
    default:
      return true;
  }
}

export function maxKids(answers: Answers): number {
  return answers.maritalStatus === 'married' ? 3 : 2;
}

export function score(answers: Answers): number {
  return realUScore(answers.gpa, answers.habits);
}

// Number of jobs the student actually qualifies for across every career category —
// exactly the jobs the career picker leaves unlocked. Mirror its lock rule
// (locked = j.req > score) negated, so this count can never drift from the picker.
export function offers(answers: Answers): number {
  const sc = realUScore(answers.gpa, answers.habits);
  return CATEGORIES.reduce((n, c) => n + c.jobs.filter((j) => !(j.req > sc)).length, 0);
}
